import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import { produce } from 'immer';

import { useSnackbar } from '@/features/snackbar';

import {
  PORTFOLIOS_QUERY_KEY,
  PORTFOLIO_QUERY_KEY,
} from '@/hooks/usePortfolio';
import type {
  PortfolioQueryClientData,
  PortfoliosQueryClientData,
} from '@/hooks/usePortfolio';

import { renamePortfolioQueryFn } from './renamePortfolioQueryFn';

export function useRenamePortfolio() {
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { addSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: renamePortfolioQueryFn,
    onMutate: (payload) => {
      addSnackbar({
        type: 'loading',
        message: 'Renaming portfolio...',
        lifeTime: 2000,
      });
      /** Optimistically rename portfolio */
      queryClient.setQueriesData<PortfolioQueryClientData>(
        { queryKey: [PORTFOLIO_QUERY_KEY, payload.id] },
        (state) =>
          produce(state, (draft) => {
            if (typeof draft === 'undefined') {
              return draft;
            }
            draft.name = payload.name;
          })
      );
      queryClient.setQueryData<PortfoliosQueryClientData>(
        PORTFOLIOS_QUERY_KEY,
        (state) =>
          produce(state, (draft) => {
            if (typeof draft === 'undefined') {
              return draft;
            }
            const renamedPortfolio = draft.find(({ id }) => id === payload.id);
            if (renamedPortfolio) {
              renamedPortfolio.name = payload.name;
            }
          })
      );
    },
    onSuccess: () => {
      trackEvent({
        action: 'success',
        modifier: 'renamePortfolio',
      });
      addSnackbar({
        type: 'positive',
        message: `Portfolio successfully renamed`,
        lifeTime: 2000,
      });
    },
    onSettled: (_, __, payload) => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEY, payload.id],
      });
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        message: 'Failed to rename portfolio',
        lifeTime: 2000,
      });
    },
  });

  return mutation;
}
